import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Dropdown, Input, Spin, Tooltip, Typography } from 'antd'

import StoryMapDeleteConfirm from './ProjectStoryMapBoard/components/StoryMapDeleteConfirm'
import ProjectStoryMapBoard from './ProjectStoryMapBoard'
import StoryMapBoardSkeleton from './ProjectStoryMapBoard/components/StoryMapBoardSkeleton'
import {
    createStoryMap,
    deleteStoryMap,
    exportStoryMapExcel,
    formatStoryMapMessage,
    getStoryMapDetail,
    listStoryMapsByProject,
    updateStoryMap,
} from '../storyMap.service'
import { showActionNotification } from '../../../components/notification'
import {
    STORY_MAP_TAB_BAR_HOST_ATTR,
    STORY_MAP_TAB_BAR_SLOT_ATTR,
    attachTabBarSlot,
    removeTabBarHost,
} from './projectTabBarPortal'
import styles from './ProjectStoryMap.module.css'

const { Text } = Typography

const STORY_MAP_STATS = [
    {
        key: 'journeysCount',
        label: 'Journey',
        plural: 'Journeys',
        icon: 'mdi:map-marker-path',
        tone: 'journey',
    },
    {
        key: 'stepsCount',
        label: 'Step',
        plural: 'Steps',
        icon: 'mdi:stairs',
        tone: 'step',
    },
    {
        key: 'releasesCount',
        label: 'Release',
        plural: 'Releases',
        icon: 'mdi:rocket-launch-outline',
        tone: 'release',
    },
    {
        key: 'storiesCount',
        label: 'Story',
        plural: 'Stories',
        icon: 'mdi:book-open-page-variant-outline',
        tone: 'story',
    },
]

const CARD_THEMES = [
    { accent: '#4f7df3', icon: '#3b6fd9' },
    { accent: '#0d9488', icon: '#0f766e' },
    { accent: '#7c3aed', icon: '#6d28d9' },
    { accent: '#ea580c', icon: '#c2410c' },
]

const getCardTheme = (title = '') => {
    let hash = 0
    for (let i = 0; i < title.length; i++) {
        hash = title.charCodeAt(i) + ((hash << 5) - hash)
    }
    return CARD_THEMES[Math.abs(hash) % CARD_THEMES.length]
}

const StoryMapCard = ({
    storyMap,
    onOpen,
    onRenameSave,
    onDelete,
    onExport,
    readOnly = false,
    canDelete = false,
    exporting = false,
}) => {
    const theme = getCardTheme(storyMap.title)
    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState(storyMap.title || '')
    const [saving, setSaving] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const titleInputRef = useRef(null)
    const skipBlurSaveRef = useRef(false)

    useEffect(() => {
        if (!isEditing) {
            setDraft(storyMap.title || '')
        }
    }, [storyMap.title, isEditing])

    useEffect(() => {
        if (!isEditing) return undefined

        const frame = window.requestAnimationFrame(() => {
            titleInputRef.current?.focus()
            titleInputRef.current?.select()
        })

        return () => window.cancelAnimationFrame(frame)
    }, [isEditing])

    const startEditing = (event) => {
        event?.stopPropagation()
        if (readOnly || saving) return
        setDraft(storyMap.title || '')
        setIsEditing(true)
    }

    const cancelEditing = () => {
        setDraft(storyMap.title || '')
        setIsEditing(false)
    }

    const commitRename = async () => {
        if (saving) return

        const trimmed = draft.trim() || 'Untitled Story Map'
        if (trimmed === (storyMap.title || '')) {
            setIsEditing(false)
            return
        }

        setSaving(true)
        const success = await onRenameSave?.(storyMap.id, trimmed)
        setSaving(false)

        if (success !== false) {
            setIsEditing(false)
        }
    }

    const handleTitleKeyDown = (event) => {
        event.stopPropagation()

        if (event.key === 'Enter') {
            event.preventDefault()
            skipBlurSaveRef.current = true
            commitRename()
            return
        }

        if (event.key === 'Escape') {
            event.preventDefault()
            skipBlurSaveRef.current = true
            cancelEditing()
        }
    }

    const handleTitleBlur = () => {
        if (skipBlurSaveRef.current) {
            skipBlurSaveRef.current = false
            return
        }
        commitRename()
    }

    const menuItems = [
        {
            key: 'export',
            label: exporting ? 'Exporting…' : 'Export Storymap',
            icon: <Icon icon="mdi:file-excel-outline" width={16} />,
            disabled: exporting,
        },
        ...(!readOnly && canDelete
            ? [
                  {
                      key: 'delete',
                      label: 'Delete',
                      danger: true,
                      icon: <Icon icon="mdi:delete-outline" width={16} />,
                  },
              ]
            : []),
    ]

    const handleMenuClick = ({ key, domEvent }) => {
        domEvent?.stopPropagation()

        if (key === 'export') {
            onExport?.(storyMap)
            return
        }

        if (key === 'delete') {
            onDelete?.(storyMap)
        }
    }

    const handleCardClick = () => {
        if (isEditing || saving) return
        onOpen?.(storyMap.id)
    }

    return (
        <article
            className={`${styles.card} ${isEditing ? styles.cardEditing : ''}`}
            style={{ '--card-accent': theme.accent }}
            onClick={handleCardClick}
        >
            <div className={styles.cardBody}>
                <div className={styles.cardHeader}>
                    <div
                        className={styles.cardIcon}
                        style={{ color: theme.icon }}
                    >
                        <Icon icon="mdi:map-marker-path" width={22} />
                    </div>
                    <div className={styles.cardInfo}>
                        {isEditing ? (
                            <div
                                className={styles.cardTitleEditWrap}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Input
                                    ref={titleInputRef}
                                    value={draft}
                                    onChange={(event) =>
                                        setDraft(event.target.value)
                                    }
                                    onKeyDown={handleTitleKeyDown}
                                    onBlur={handleTitleBlur}
                                    className={styles.cardTitleInput}
                                    placeholder="Story map name"
                                    maxLength={120}
                                    disabled={saving}
                                    suffix={
                                        saving ? (
                                            <Spin size="small" />
                                        ) : null
                                    }
                                />
                            </div>
                        ) : (
                            <>
                                <Tooltip
                                    title={storyMap.title}
                                    placement="topLeft"
                                >
                                    <button
                                        type="button"
                                        className={styles.cardTitleBtn}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onOpen?.(storyMap.id)
                                        }}
                                        onDoubleClick={startEditing}
                                    >
                                        <span
                                            className={styles.cardTitleText}
                                        >
                                            {storyMap.title}
                                        </span>
                                    </button>
                                </Tooltip>
                                <span className={styles.cardSubtitle}>
                                    {storyMap.authorName || 'Unknown'}
                                </span>
                            </>
                        )}
                    </div>
                    {!readOnly && !isEditing ? (
                        <div
                            className={`${styles.cardActions} ${
                                menuOpen ? styles.cardActionsOpen : ''
                            }`.trim()}
                        >
                            <button
                                type="button"
                                className={styles.actionIconBtn}
                                aria-label="Rename story map"
                                onClick={startEditing}
                            >
                                <Icon
                                    icon="mynaui:edit"
                                    width={18}
                                    height={18}
                                />
                            </button>
                            {menuItems.length > 0 ? (
                                <Dropdown
                                    menu={{
                                        items: menuItems,
                                        onClick: handleMenuClick,
                                    }}
                                    trigger={['click']}
                                    open={menuOpen}
                                    onOpenChange={setMenuOpen}
                                    placement="bottomRight"
                                    overlayClassName={
                                        styles.cardMenuDropdown
                                    }
                                >
                                    <button
                                        type="button"
                                        className={`${styles.actionIconBtn} ${
                                            menuOpen
                                                ? styles.actionIconBtnActive
                                                : ''
                                        }`.trim()}
                                        aria-label="Story map options"
                                        aria-expanded={menuOpen}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Icon
                                            icon="mdi:dots-vertical"
                                            width={18}
                                        />
                                    </button>
                                </Dropdown>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                <div className={styles.statsSection}>
                    <div className={styles.statsHeader}>
                        <div className={styles.statsHeaderLeft}>
                            <Icon
                                icon="mdi:chart-box-outline"
                                className={styles.statsIcon}
                            />
                            <span className={styles.statsLabel}>Overview</span>
                        </div>
                        {storyMap.createdDate ? (
                            <span className={styles.cardDate}>
                                <Icon
                                    icon="mdi:calendar-outline"
                                    width={12}
                                />
                                {storyMap.createdDate}
                            </span>
                        ) : null}
                    </div>
                    <div className={styles.cardStatsGrid}>
                        {STORY_MAP_STATS.map(
                            ({ key, label, plural, tone }) => (
                                <div key={key} className={styles.cardStatCell}>
                                    <span
                                        className={`${styles.statCountWrap} ${styles[`statIcon_${tone}`]}`}
                                    >
                                        {storyMap[key]}
                                    </span>
                                    <span className={styles.statName}>
                                        {storyMap[key] === 1 ? label : plural}
                                    </span>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        </article>
    )
}

const StoryMapToolbar = ({ search, onSearchChange, onCreate, creating, readOnly = false }) => {
    const [isSearchExpanded, setIsSearchExpanded] = useState(false)
    const searchWrapperRef = useRef(null)
    const searchInputRef = useRef(null)

    const handleSearchIconClick = () => {
        setIsSearchExpanded(true)
        window.setTimeout(() => {
            const inputElement =
                searchInputRef.current?.input || searchInputRef.current
            inputElement?.focus?.()
        }, 150)
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchWrapperRef.current &&
                !searchWrapperRef.current.contains(event.target) &&
                isSearchExpanded &&
                !search.trim()
            ) {
                setIsSearchExpanded(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isSearchExpanded, search])

    return (
        <div className={styles.tabBarToolbar}>
            {!readOnly ? (
                <button
                    type="button"
                    className={styles.createButton}
                    onClick={onCreate}
                    disabled={creating}
                >
                    <Icon icon="mdi:plus" width={16} height={16} />
                    <span>{creating ? 'Creating...' : 'Create Story Map'}</span>
                </button>
            ) : null}

            <div
                ref={searchWrapperRef}
                className={`${styles.searchWrapper} ${
                    isSearchExpanded ? styles.searchExpanded : ''
                }`}
            >
                {!isSearchExpanded ? (
                    <Tooltip title="Search story maps">
                        <div
                            className={styles.searchIconButton}
                            onClick={handleSearchIconClick}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSearchIconClick()
                            }}
                            role="button"
                            tabIndex={0}
                        >
                            <Icon
                                icon="mdi:magnify"
                                width={18}
                                height={18}
                                className={styles.searchIcon}
                            />
                        </div>
                    </Tooltip>
                ) : (
                    <Input
                        ref={searchInputRef}
                        allowClear
                        placeholder="Search story maps"
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className={styles.searchInput}
                        prefix={
                            <Icon
                                icon="mdi:magnify"
                                width={18}
                                height={18}
                                className={styles.searchIcon}
                            />
                        }
                    />
                )}
            </div>
        </div>
    )
}

const TAB_BAR_HOST_ATTR = STORY_MAP_TAB_BAR_HOST_ATTR
const TAB_BAR_SLOT_ATTR = STORY_MAP_TAB_BAR_SLOT_ATTR

const STORY_MAP_PAGE_SIZE = 25

const ProjectStoryMap = ({ projectId, readOnly = false, canDelete = false }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const [search, setSearch] = useState('')
    const [selectedStoryMapId, setSelectedStoryMapId] = useState(null)
    const [activeBoard, setActiveBoard] = useState(null)
    const [boardLoading, setBoardLoading] = useState(false)
    const [tabBarSlot, setTabBarSlot] = useState(null)
    const [useInlineToolbar, setUseInlineToolbar] = useState(false)
    const [storyMaps, setStoryMaps] = useState([])
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [isNext, setIsNext] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [creating, setCreating] = useState(false)
    const [deleteConfirm, setDeleteConfirm] = useState(null)
    const [exportingStoryMapId, setExportingStoryMapId] = useState(null)
    const searchDebounceRef = useRef(null)
    const openingStoryMapRef = useRef(null)

    const updateStoryMapRoute = useCallback(
        (storyMapId, { clearCardLink = false } = {}) => {
            const params = new URLSearchParams(location.search)

            if (storyMapId) {
                params.set('storyMapId', storyMapId)
            } else {
                params.delete('storyMapId')
            }

            if (clearCardLink) {
                params.delete('stepId')
                params.delete('storyId')
            }

            const nextSearch = params.toString()
            navigate(
                {
                    pathname: location.pathname,
                    search: nextSearch ? `?${nextSearch}` : '',
                },
                { replace: true }
            )
        },
        [location.pathname, location.search, navigate]
    )

    const clearCardDeepLinkParams = useCallback(() => {
        const params = new URLSearchParams(location.search)
        if (!params.get('stepId') && !params.get('storyId')) return

        params.delete('stepId')
        params.delete('storyId')

        const nextSearch = params.toString()
        navigate(
            {
                pathname: location.pathname,
                search: nextSearch ? `?${nextSearch}` : '',
            },
            { replace: true }
        )
    }, [location.pathname, location.search, navigate])

    const cardDeepLink = useMemo(() => {
        const params = new URLSearchParams(location.search)
        const storyId = params.get('storyId')
        const stepId = params.get('stepId')

        if (storyId) {
            return { type: 'story', id: storyId }
        }
        if (stepId) {
            return { type: 'step', id: stepId }
        }
        return null
    }, [location.search])

    const openStoryMap = useCallback(
        async (storyMapId) => {
            if (!storyMapId || openingStoryMapRef.current === storyMapId) return

            openingStoryMapRef.current = storyMapId
            setSelectedStoryMapId(storyMapId)
            setActiveBoard(null)
            setBoardLoading(true)
            updateStoryMapRoute(storyMapId)

            const result = await getStoryMapDetail(storyMapId)

            if (openingStoryMapRef.current !== storyMapId) return

            openingStoryMapRef.current = null
            setBoardLoading(false)

            if (!result.success || !result.board) {
                setSelectedStoryMapId(null)
                setActiveBoard(null)
                updateStoryMapRoute(null, { clearCardLink: true })
                showActionNotification({
                    type: 'error',
                    message: formatStoryMapMessage(
                        result.message,
                        'Failed to load story map'
                    ),
                })
                return
            }

            setActiveBoard(result.board)
        },
        [updateStoryMapRoute]
    )

    const handleBackFromBoard = useCallback(() => {
        openingStoryMapRef.current = null
        setSelectedStoryMapId(null)
        setActiveBoard(null)
        setBoardLoading(false)
        updateStoryMapRoute(null, { clearCardLink: true })
    }, [updateStoryMapRoute])

    useEffect(() => {
        if (selectedStoryMapId) {
            removeTabBarHost(TAB_BAR_HOST_ATTR)
            setTabBarSlot(null)
            setUseInlineToolbar(false)
            return undefined
        }

        let timer = null
        let retryTimer = null

        const attachToolbar = () => {
            const slot = attachTabBarSlot({
                hostAttr: TAB_BAR_HOST_ATTR,
                slotAttr: TAB_BAR_SLOT_ATTR,
                hostClassName: styles.tabBarHost,
            })
            if (!slot) return false

            setTabBarSlot(slot)
            setUseInlineToolbar(false)
            return true
        }

        if (!attachToolbar()) {
            timer = window.setTimeout(attachToolbar, 0)
            retryTimer = window.setTimeout(() => {
                if (!attachToolbar()) {
                    setUseInlineToolbar(true)
                }
            }, 120)
        }

        return () => {
            if (timer) window.clearTimeout(timer)
            if (retryTimer) window.clearTimeout(retryTimer)

            removeTabBarHost(TAB_BAR_HOST_ATTR)
            setTabBarSlot(null)
            setUseInlineToolbar(false)
        }
    }, [selectedStoryMapId])

    const fetchStoryMaps = useCallback(
        async ({ skip = 0, append = false, searchTerm = search } = {}) => {
            if (!projectId) {
                setStoryMaps([])
                setIsNext(false)
                return
            }

            if (append) {
                setLoadingMore(true)
            } else {
                setLoading(true)
            }
            setLoadError('')

            const result = await listStoryMapsByProject({
                storyMapId: projectId,
                limit: STORY_MAP_PAGE_SIZE,
                skip,
                search: searchTerm,
            })

            if (!result.success) {
                if (!append) {
                    setStoryMaps([])
                }
                setLoadError(formatStoryMapMessage(result.message, 'Failed to load story maps'))
                setIsNext(false)
            } else {
                setStoryMaps((prev) =>
                    append ? [...prev, ...result.data] : result.data
                )
                setIsNext(result.isNext)
            }

            setLoading(false)
            setLoadingMore(false)
        },
        [projectId, search]
    )

    useEffect(() => {
        if (selectedStoryMapId) return undefined

        if (searchDebounceRef.current) {
            window.clearTimeout(searchDebounceRef.current)
        }

        searchDebounceRef.current = window.setTimeout(() => {
            fetchStoryMaps({ skip: 0, append: false, searchTerm: search })
        }, search.trim() ? 300 : 0)

        return () => {
            if (searchDebounceRef.current) {
                window.clearTimeout(searchDebounceRef.current)
            }
        }
    }, [projectId, search, selectedStoryMapId, fetchStoryMaps])

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const routeStoryMapId = params.get('storyMapId')

        if (!routeStoryMapId || routeStoryMapId === selectedStoryMapId) {
            return
        }

        openStoryMap(routeStoryMapId)
    }, [location.search, openStoryMap, selectedStoryMapId])

    const handleLoadMore = useCallback(() => {
        if (!isNext || loadingMore) return
        fetchStoryMaps({
            skip: storyMaps.length,
            append: true,
            searchTerm: search,
        })
    }, [isNext, loadingMore, storyMaps.length, search, fetchStoryMaps])

    const scrollSentinelRef = useRef(null)

    useEffect(() => {
        const sentinel = scrollSentinelRef.current
        if (!sentinel) return undefined

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    handleLoadMore()
                }
            },
            { rootMargin: '200px' }
        )

        observer.observe(sentinel)

        return () => observer.disconnect()
    }, [handleLoadMore])

    const handleCreateStoryMap = async () => {
        if (!projectId || creating || readOnly) return

        setCreating(true)

        const result = await createStoryMap({
            name: 'Untitled Story Map',
            projectId,
        })

        setCreating(false)

        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to create story map',
            })
            return
        }

        showActionNotification({
            type: 'success',
            message: 'Story Map created successfully',
        })

        fetchStoryMaps({ skip: 0, append: false, searchTerm: search })
    }

    const handleRenameSave = async (storyMapId, trimmed) => {
        const result = await updateStoryMap(storyMapId, { name: trimmed })

        if (!result.success) {
            showActionNotification({
                message: result.message || 'Failed to rename story map',
            })
            return false
        }

        setStoryMaps((prev) =>
            prev.map((item) =>
                item.id === storyMapId ? { ...item, title: trimmed } : item
            )
        )
        return true
    }

    const handleDeleteStoryMap = (storyMap) => {
        if (readOnly || !canDelete) return

        setDeleteConfirm({
            title: 'Delete this story map?',
            message:
                'This story map will be removed from the project. This cannot be undone.',
            onConfirm: async () => {
                const result = await deleteStoryMap(storyMap.id)

                if (!result.success) {
                    showActionNotification({
                        type: 'error',
                        message: result.message || 'Failed to delete story map',
                    })
                    return
                }

                setStoryMaps((prev) => prev.filter((item) => item.id !== storyMap.id))
                showActionNotification({
                    type: 'success',
                    message: result.message || 'Story map deleted successfully',
                })
                setDeleteConfirm(null)
            },
        })
    }

    const handleExportStoryMap = async (storyMap) => {
        if (!storyMap?.id || exportingStoryMapId) return

        setExportingStoryMapId(storyMap.id)
        const safeTitle = String(storyMap.title || 'StoryMap')
            .replace(/[^a-zA-Z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
        const result = await exportStoryMapExcel(storyMap.id, {
            fileName: safeTitle ? `${safeTitle}.xlsx` : undefined,
        })
        setExportingStoryMapId(null)

        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to export story map',
            })
            return
        }

        showActionNotification({
            type: 'success',
            message: result.message || 'Story map exported successfully',
        })
    }

    const filteredStoryMaps = storyMaps

    if (selectedStoryMapId) {
        if (boardLoading || !activeBoard) {
            return (
                <div className={styles.boardHost}>
                    <StoryMapBoardSkeleton />
                </div>
            )
        }

        return (
            <div className={styles.boardHost}>
                <ProjectStoryMapBoard
                    key={selectedStoryMapId}
                    board={activeBoard}
                    projectId={projectId}
                    onBack={handleBackFromBoard}
                    initialDeepLink={cardDeepLink}
                    onDeepLinkHandled={clearCardDeepLinkParams}
                    readOnly={readOnly}
                    canDelete={canDelete}
                />
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {tabBarSlot && !selectedStoryMapId
                ? createPortal(
                      <StoryMapToolbar
                          search={search}
                          onSearchChange={setSearch}
                          onCreate={handleCreateStoryMap}
                          creating={creating}
                          readOnly={readOnly}
                      />,
                      tabBarSlot
                  )
                : null}

            {useInlineToolbar ? (
                <div className={styles.inlineToolbar}>
                    <StoryMapToolbar
                        search={search}
                        onSearchChange={setSearch}
                        onCreate={handleCreateStoryMap}
                        creating={creating}
                        readOnly={readOnly}
                    />
                </div>
            ) : null}

            <div className={styles.content}>
                {loading && filteredStoryMaps.length === 0 ? (
                    <div className={styles.loadingState}>
                        <Icon
                            icon="line-md:loading-loop"
                            style={{ fontSize: 48, color: '#6366f1' }}
                        />
                    </div>
                ) : filteredStoryMaps.length > 0 ? (
                    <>
                        <div className={styles.grid}>
                            {filteredStoryMaps.map((storyMap) => (
                                <StoryMapCard
                                    key={storyMap.id}
                                    storyMap={storyMap}
                                    onOpen={openStoryMap}
                                    onRenameSave={handleRenameSave}
                                    onDelete={handleDeleteStoryMap}
                                    onExport={handleExportStoryMap}
                                    readOnly={readOnly}
                                    canDelete={canDelete}
                                    exporting={
                                        String(exportingStoryMapId) ===
                                        String(storyMap.id)
                                    }
                                />
                            ))}
                        </div>
                        {isNext ? (
                            <div
                                ref={scrollSentinelRef}
                                className={styles.loadMoreWrap}
                            >
                                {loadingMore ? (
                                    <Icon
                                        icon="line-md:loading-loop"
                                        style={{ fontSize: 24, color: '#6366f1' }}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIconWrap}>
                            <Icon
                                icon="mdi:map-marker-path"
                                width={26}
                                className={styles.emptyIcon}
                            />
                        </div>
                        <Text className={styles.emptyTitle}>
                            {loadError || 'No story maps found'}
                        </Text>
                        <Text className={styles.emptyText}>
                            {loadError
                                ? 'Please try again later.'
                                : search
                                  ? 'Try a different search term.'
                                  : readOnly
                                    ? 'No story maps are available yet.'
                                    : 'Create your first story map to get started.'}
                        </Text>
                        {!search && !loadError && !readOnly && (
                            <button
                                type="button"
                                className={styles.emptyCta}
                                onClick={handleCreateStoryMap}
                                disabled={creating}
                            >
                                <Icon icon="mdi:plus" width={16} />
                                {creating ? 'Creating...' : 'Create Story Map'}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <StoryMapDeleteConfirm
                open={Boolean(deleteConfirm)}
                title={deleteConfirm?.title}
                message={deleteConfirm?.message}
                onCancel={() => setDeleteConfirm(null)}
                onConfirm={deleteConfirm?.onConfirm}
            />
        </div>
    )
}

export default ProjectStoryMap
